import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import Session from '@utils/Session'

import { getAssetManifest, getCsrfToken, getServerSideData } from './server-side'

import type { ServerData } from './server-side'

/**
 * GlobalProvider can be used throughout the app to obtain data that is bootstrapped on page load
 * The contextValue that is returned when useGlobal() is invoked contains:
 *    globalData for app-wide state such as session information
 *
 * The data that gets bootstrapped for a given route and passed to pageData should be identical
 * to the response of API requests to the same route. This allows us to show bootstrapped data on pageload
 * and fetch the same data when navigating to a route through react
 */
export type GlobalData = {
  session: Session
  locale?: string
  assetPath?: string
  stripePublicKey: string
  manifest_url?: string
  application_version?: string
}

export interface GlobalDataState {
  serverData: ServerData | null
  globalData: GlobalData
  assetManifest: Record<string, string> | null
  csrfToken: string
}

export function parseGlobalData(serverData?: ServerData | null): GlobalData {
  if (!serverData) throw new Error('Missing bootstrap data')
  return {
    session: new Session(serverData.login, serverData.client_settings),
    assetPath: serverData.assetPath,
    locale: serverData.locale,
    manifest_url: serverData.manifest_url,
    application_version: serverData.application_version,
    stripePublicKey: serverData.stripePublicKey,
  }
}

const serverData = getServerSideData()
const globalData = parseGlobalData(serverData)
const assetManifest = getAssetManifest()
const csrfToken = getCsrfToken()

export const useGlobalDataStore = create<GlobalDataState>()(
  devtools(
    () => ({
      serverData,
      globalData,
      assetManifest,
      csrfToken,
    }),
    {
      enabled: process.env.NODE_ENV === 'development',
    }
  )
)

export const useSession = (): Session => useGlobalDataStore((s) => s.globalData.session)
